import Button from '@components/shared/Button'
import DateRangePicker from '@components/shared/DateRangePicker'
import MobileModal from '@components/shared/MobileModal'
import Modal from '@components/shared/Modal'
import Tooltip from '@components/shared/Tooltip'
import { SeriesType } from '@components/trade/rfx/tradingview/constants'
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import { CustomTimezoneId } from '@public/charting_library/charting_library'
import {
  ChartOptionsStore,
  multichartSettingsDefaults,
  useMultiChartSettings,
} from '@store/multichartSettingsStore'
import dayjs from 'dayjs'
import useBreakpoints from 'hooks/useBreakpoints'
import InfoIcon from 'public/icons/tooltip-info-icon-filled.svg'
import { ReactNode, useCallback, useState } from 'react'
import { FaChevronDown } from 'react-icons/fa'
import { cn } from 'utils/classnames'
import { zonesWithGMT } from 'utils/timezones'
import Checkbox from './Checkbox'

const DatePickerContainer = ({ children }: { children: ReactNode[] }) => {
  return (
    <div className="absolute bottom-0 left-0 z-[51] flex w-full justify-between">
      {children}
    </div>
  )
}

const barTypeOptions: Option[] = [
  {
    label: 'Bars',
    value: SeriesType.Bars,
  },
  {
    label: 'Candles',
    value: SeriesType.Candles,
  },
  {
    label: 'Line',
    value: SeriesType.Line,
  },
  {
    label: 'Area',
    value: SeriesType.Area,
  },
  {
    label: 'Heiken Ashi ',
    value: SeriesType.HeikenAshi,
  },
  {
    label: 'Hollow Candles',
    value: SeriesType.HollowCandles,
  },
  {
    label: 'Baseline',
    value: SeriesType.Baseline,
  },
  {
    label: 'HiLo',
    value: SeriesType.HiLo,
  },
]

const timezoneOptions: Option[] = [
  { label: 'Exchange', value: 'exchange' },
  ...zonesWithGMT,
]

const intervalOptions = ['1m', '5m', '15m', '1h', '4h', 'D', '1W'].map(
  (option) => ({
    label: option,
    value: option.includes('m') ? `${option.replace('m', '')}` : option,
  }),
)

const ChartOptionsModal = ({
  onClose,
  isOpen,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const { below } = useBreakpoints()
  const state = useMultiChartSettings((state) => state)

  const [startDate, setStartDate] = useState<Date | null>(
    state.dateRange[0] || dayjs(new Date()).subtract(1, 'day').toDate(),
  )
  const [endDate, setEndDate] = useState<Date | null>(
    state.dateRange[1] || new Date(),
  )

  const [draft, setDraft] = useState({
    ...state,
  })

  const {
    timezone = timezoneOptions[0].value,
    barType,
    interval,
    hideLegend,
    hideSideToolbar,
    hideTopToolbar,
    showBottomToolbar,
    useDataRange,
    set,
  } = draft

  const updateKey = useCallback(
    (key: keyof typeof draft, value: ChartOptionsStore[keyof typeof draft]) => {
      setDraft((prev) => ({ ...prev, [key]: value }))
    },
    [setDraft],
  )

  const reset = useCallback(() => {
    set(multichartSettingsDefaults)
    onClose()
  }, [onClose, set])

  const done = useCallback(() => {
    set({
      ...draft,
      ...(startDate && endDate
        ? {
            dateRange: [startDate, endDate],
          }
        : {}),
    })
    onClose()
  }, [draft, endDate, onClose, set, startDate])

  const foundTimezone =
    timezoneOptions.find((option) => option.value === timezone) ||
    timezoneOptions[0]

  const foundBarType =
    barTypeOptions.find((option) => option.value === barType) ||
    barTypeOptions[0]

  const renderModalContent = () => (
    <div className="relative flex h-full flex-col justify-between pt-4">
      <div>
        <div className="relative">
          <div className="flex flex-col">
            <span className="mb-2 text-xs font-medium text-th-fgd-3 fhd:text-sm">
              Timezone
            </span>

            <Dropdown
              selectedOption={foundTimezone}
              setSelectedOption={(option) =>
                updateKey('timezone', option.value as CustomTimezoneId)
              }
              options={timezoneOptions}
            />
          </div>
        </div>

        <div className="mt-4">
          <div>
            <span className="mb-2 text-xs font-medium text-th-fgd-3 fhd:text-sm">
              Bar Type
            </span>

            <Dropdown
              selectedOption={foundBarType}
              setSelectedOption={(option) =>
                updateKey('barType', option.value as SeriesType)
              }
              options={barTypeOptions}
            />
          </div>
        </div>

        <div className="my-4 w-full border-t border-th-input-border" />
        <div className="relative flex items-center justify-between">
          <span className="text-xs font-medium text-th-fgd-3 fhd:text-sm">
            Use Data Range instead of Default interval{' '}
          </span>
          <Checkbox
            isToggled={useDataRange}
            toggle={() => updateKey('useDataRange', !useDataRange)}
          />
        </div>

        <div className="mt-6">
          <div>
            <span className="mb-2 text-xs font-medium text-th-fgd-3 fhd:text-sm">
              Default Interval
            </span>

            <Dropdown
              selectedOption={
                intervalOptions.find((option) => option.value === interval) ||
                intervalOptions[0]
              }
              setSelectedOption={(option) =>
                updateKey('interval', option.value.toString())
              }
              options={intervalOptions}
            />
          </div>
        </div>

        <div className="mt-4">
          <div>
            <span className="text-xs font-medium text-th-fgd-3 fhd:text-sm">
              Date Range
            </span>

            <div className="relative mt-2 flex h-10 items-center justify-between text-xs font-semibold text-th-fgd-1 fhd:text-sm">
              <DateRangePicker
                // withPortal
                popperProps={{ strategy: 'fixed' }}
                popperContainer={DatePickerContainer}
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
              />
            </div>
          </div>
        </div>

        <div className="my-4 w-full border-t border-th-input-border" />

        <div className="mt-4">
          <div className="relative mt-4 flex items-center justify-between">
            <span className="text-xs font-medium text-th-fgd-3 fhd:text-sm">
              Show Bottom Toolbar
            </span>

            <Checkbox
              isToggled={showBottomToolbar}
              toggle={() => updateKey('showBottomToolbar', !showBottomToolbar)}
            />
          </div>
        </div>

        <div className="my-4 w-full border-t border-th-input-border" />

        <div className="mt-4">
          <div className="relative flex items-center justify-between">
            <span className="flex items-center gap-1 text-xs font-medium text-th-fgd-3 fhd:text-sm">
              Hide Top Toolbar (Symbol Change)
              <span>
                <Tooltip content="" showUnderline={false}>
                  <InfoIcon />
                </Tooltip>
              </span>
            </span>

            <Checkbox
              isToggled={hideTopToolbar}
              toggle={() => updateKey('hideTopToolbar', !hideTopToolbar)}
            />
          </div>

          <div className="relative mt-4 flex items-center justify-between">
            <span className="flex items-center gap-1 text-xs font-medium text-th-fgd-3 fhd:text-sm">
              Hide Legend
              <span>
                <Tooltip content="" showUnderline={false}>
                  <InfoIcon />
                </Tooltip>
              </span>
            </span>

            <Checkbox
              isToggled={hideLegend}
              toggle={() => updateKey('hideLegend', !hideLegend)}
            />
          </div>

          <div className="relative mt-4 flex items-center justify-between pb-4">
            <span className="flex items-center gap-1 text-xs font-medium text-th-fgd-3 fhd:text-sm">
              Hide Side Toolbar
              <span>
                <Tooltip content="" showUnderline={false}>
                  <InfoIcon />
                </Tooltip>
              </span>
            </span>

            <Checkbox
              isToggled={hideSideToolbar}
              toggle={() => updateKey('hideSideToolbar', !hideSideToolbar)}
            />
          </div>
        </div>
      </div>

      <div className="relative max-sm:space-y-4 sm:flex sm:items-center sm:justify-between">
        <Button
          secondary
          className="chart-options-button-width flex cursor-pointer items-center justify-center border-th-fgd-3 !py-3 !text-th-fgd-1"
          onClick={reset}
          size={below.xl ? 'medium' : 'large'}
        >
          RESET
        </Button>

        <Button
          onClick={done}
          className="chart-options-button-width flex cursor-pointer items-center justify-center !bg-th-active !py-3 !text-black"
          size={below.xl ? 'medium' : 'large'}
        >
          DONE
        </Button>
      </div>
    </div>
  )

  if (below.sm) {
    return (
      <MobileModal isOpen={isOpen} onClose={onClose} title="RFX Charts">
        {renderModalContent()}
      </MobileModal>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="RFX Charts"
      hasSpaceBtwCloseAndTitle={false}
    >
      {renderModalContent()}
    </Modal>
  )
}

export type Option = { label: string; value: string | number }

export const Dropdown = ({
  options,
  selectedOption,
  setSelectedOption,
  hasBg = true,
  buttonClassName,
}: {
  options: Option[]
  selectedOption: Option
  setSelectedOption: (option: Option) => void
  hasBg?: boolean
  buttonClassName?: string
}) => {
  return (
    <Listbox
      value={selectedOption}
      onChange={(option) => setSelectedOption(option)}
    >
      <div className="relative w-full data-[headlessui-state=open]:z-30">
        <ListboxButton className="w-full">
          <div
            className={cn(
              `text-th-fgd-1, flex h-[30px] items-center justify-between px-2 text-xs font-semibold fhd:text-sm ${hasBg && 'bg-th-bkg-2'}`,
              buttonClassName,
            )}
          >
            <span>{selectedOption.label}</span>
            <span className={cn('scale-75 text-th-fgd-2')}>
              <FaChevronDown />
            </span>
          </div>
        </ListboxButton>
        <ListboxOptions className="absolute -bottom-2 right-[2px] z-20 mt-1 max-h-[200px] w-fit translate-y-full overflow-y-auto rounded-md border border-th-input-border bg-th-bkg-5 px-2 text-xs font-semibold text-th-fgd-1 fhd:text-sm">
          {options.map((option) => (
            <ListboxOption
              className="cursor-pointer border-b border-th-input-border p-2 last-of-type:!border-none hover:text-th-fgd-1"
              key={option.label}
              value={option}
            >
              {option.label}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  )
}

export default ChartOptionsModal
